
import { defineComponent, onBeforeMount, reactive } from 'vue'

// Components
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { WarehouseT } from '@/core/types/warehouses.types'

// Realisations
import { warehouses } from '@/core/realisations/warehouses.realisation'
import { TmcT } from '@/core/types/tmc.types'
import { tmc } from '@/core/realisations/tmc.realisation'
import { companies } from '@/core/realisations/companies.realisation'
import { CompanyT } from '@/core/types/companies.types'

// Constants
const TYPES = [
    {
        id: 1,
        name: 'По накладной',
    },
    {
        id: 2,
        name: 'Складской расход',
    },
    {
        id: 3,
        name: 'Проекты',
    },
]

const ADDITIONAL_SERVICES = [
    {
        id: 4,
        name: 'Мягкая упаковка',
    },
    {
        id: 5,
        name: 'Паллетирование',
    },
    {
        id: 6,
        name: 'Обрешетка',
    },
]

export default defineComponent({
    components: {
        TheButton,
        TheSelect,
        TheInput,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const state = reactive({
            additionalServiceTypeId: null as number | null,
            code1C: null as string | null,
            warehouseId: null as number | null,
            writeOffTypeId: null as number | null,
            invoiceNumber: null as string | null,
            dateFrom: null as string | null,
            dateTo: null as string | null,
            inventoryItemIds: [] as number[],
            payerCompanyIds: [] as number[],
        })

        const data_state = reactive({
            warehouses: new DataState<WarehouseT>({}),
            tmc: new DataState<TmcT>({}),
            companies: new DataState<CompanyT>({}),
        })

        const resetFilters = () => {
            state.additionalServiceTypeId = null
            state.code1C = null
            state.warehouseId = null
            state.writeOffTypeId = null
            state.invoiceNumber = null
            state.dateFrom = null
            state.dateTo = null
            state.inventoryItemIds = []
            state.payerCompanyIds = []
        }

        //

        const loadWarehouses = async () => {
            try {
                data_state.warehouses.is_loading = true

                const response = await warehouses.getWarehouses()
                data_state.warehouses.list = response.data

                data_state.warehouses.is_failed = false
                data_state.warehouses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.warehouses.failure.catchError(error)

                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_failed = true
                }
            } finally {
                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_loading = false
                }
            }
        }

        const loadTMC = async (page: number, search?: string) => {
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true
                const response = await tmc.getList(page, search)
                data_state.tmc.list = response.data

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const loadMoreTMC = async (page: number) => {
            if (page > data_state.tmc.pagination.total_pages) return
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true

                const response = await tmc.getList(page)
                data_state.tmc.list = data_state.tmc.list.concat(response.data)

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const searchTMC = (search_query: string) => {
            loadTMC(1, search_query)
        }

        const unshiftFindedTMC = async (tmc: TmcT) => {
            await loadTMC(data_state.tmc.pagination.current_page)
            data_state.tmc.list.unshift(tmc)
        }

        const loadCompanies = async () => {
            data_state.companies.pagination.current_page = 1

            try {
                data_state.companies.is_loading = true
                const data = await companies.getList({
                    page: 1,
                    limit: 9999,
                    isExtended: 0,
                })

                data_state.companies.list = data.data

                data_state.companies.pagination.total_pages = data.meta.last_page
                data_state.companies.pagination.total_items = data.meta.total

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        //

        onBeforeMount(() => {
            loadWarehouses()
            loadTMC(1)
            loadCompanies()
        })

        const onSubmit = () => {
            if (props.loading) return

            emit('submit', { ...state })
        }

        const onReset = () => {
            if (props.loading) return

            resetFilters()
            emit('reset')
        }

        return {
            state,
            data_state,
            //
            loadWarehouses,
            loadTMC,
            loadMoreTMC,
            searchTMC,
            unshiftFindedTMC,
            loadCompanies,
            //
            onSubmit,
            onReset,
            //
            TYPES,
            ADDITIONAL_SERVICES,
        }
    },
})
